import React from "react";
import { SafeLink } from "../../ui/SafeLink";
import { Phone, Mail, MapPin, HeadphonesIcon, MessageSquare, Building2, Linkedin } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../../ui/card";
import { Section } from "../../ui/section";
import { ScrollContainer } from "../../ui/scroll-container";
import ContactFormSheet from "../../sliders/ContactFormSheet";
import { CTAGeneric } from "@/components/cta/"
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
// import EmailPreferenceForm from "@/components/footer/EmailPreferenceForm";
import CategoryShowcaseGrid from "../Product/showcase/CategoryShowcaseGrid";
import ShowcaseSubmenuDropdown from "../Product/showcase/dropdown/ShowcaseSubmenuDropdown"; 
import IndKitchen from "../ContentPages/WhatWeDo/Kitchen/main";


// Suppress ResizeObserver errors globally
if (typeof window !== 'undefined') {
    const resizeObserverLoopErrRe = /^[^(ResizeObserver loop limit exceeded)]/;
    const resizeObserverErrRe = /ResizeObserver loop completed with undelivered notifications/;
    window.addEventListener('error', (e) => {
        if (resizeObserverErrRe.test(e.message) || resizeObserverLoopErrRe.test(e.message)) {
            e.stopImmediatePropagation();
        }
    });
}

export default function ContactUs() {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    const [openContactSheet, setOpenContactSheet] = React.useState(false);
    // For testing products dropdown
    const [showDropdown, setShowDropdown] = React.useState(false);
    return (
        // <main>
        <ScrollContainer snapType="y" snapBehavior="mandatory" key={location.pathname}>
            {/* Hero Section */}
            <Section
                fullHeight={true}
                scrollSnap={true}
                variant="dark"
                backgroundImage="https://hncnzwww.blob.core.windows.net/www/img/Reception-area-with-harvey-norman-commercial-logo.webp"
                backgroundBlur={2}
                overlayOpacity={30}
                contentPosition="center"
                contained={false}
                padding="lg"
            >

                <div className="flex flex-col items-center justify-center min-h-[60vh] mx-auto text-center text-white max-w-4xl w-full">
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 inline-block text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                        Let's Talk <span className="text-harvey-red relative [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">Business</span>
                    </h1>
                    <p className="text-sm sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 max-w-2xl mx-auto px-4 inline-block text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                        We're here to support your next move – big or small
                    </p>
                    <ContactFormSheet buttonText="Get in Touch" buttonClassName="btn" />
                </div>
            </Section>

            {/* Contact Information */}
            <Section
                fullHeight={true}
                scrollSnap={true}
                variant="gradient-gray"
                contentPosition="center"
                padding="xl"
                contained={true}
                overlayColor="transparent"
            >
                <div className="text-center mb-12 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Other Ways to <span className="text-gradient-red">Connect</span>
                    </h2>
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                        Choose the method that works best for you.
                    </p>
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                        Our team is ready to help during business hours.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 ipadpro-portrait:grid-cols-2 lg:grid-cols-4  gap-8 lg:gap-6">
                    {/* Phone */}
                    <div className="group relative h-full">
                        <Card className="h-full min-h-[450px] bg-gray-100 hover:shadow-xl transition-all duration-300 border-gray-300 hover:border-harvey-red/20 transform hover:-translate-y-2 flex flex-col">
                            <CardHeader className="text-center pb-4">
                                <div className="relative flex justify-center mb-4">
                                    <div className="contact-icon-phone ">
                                        <HeadphonesIcon className="w-10 h-10 text-white" />
                                    </div>
                                    <div className="contact-status-indicator contact-status-red">
                                        <div className="contact-status-dot animate"></div>
                                    </div>
                                </div>
                                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">Call Us Direct</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center flex-1 px-6">
                                <CardDescription className="text-gray-600 leading-relaxed text-base mb-8">
                                    Speak with our technology experts for immediate assistance and personalised solutions. Get real-time answers to your questions.
                                </CardDescription>
                            </CardContent>
                            <CardFooter className="flex flex-col space-y-3 pt-0 px-6 pb-6">
                                <Button
                                    asChild
                                    className="w-full bg-harvey-red hover:bg-harvey-red/90 text-white font-semibold py-3 px-6 rounded-xl text-lg h-12 btn"
                                >
                                    <a href="tel:+64800222699">
                                        <Phone className="w-5 h-5 mr-2" />
                                        0800 222 699
                                    </a>
                                </Button>
                                <p className="text-xs text-gray-500">
                                    Mon-Fri 8:30AM - 5:30PM NZST
                                </p>
                            </CardFooter>
                        </Card>
                    </div>

                    {/* Email */}
                    <div className="group relative h-full">
                        <Card className="h-full min-h-[450px] bg-gray-100 hover:shadow-xl transition-all duration-300 border-gray-100 hover:border-harvey-red/20 transform hover:-translate-y-2 flex flex-col">
                            <CardHeader className="text-center pb-4">
                                <div className="relative flex justify-center mb-4">
                                    <div className="contact-icon-email  ">
                                        <MessageSquare className="w-10 h-10 text-white " />
                                    </div>
                                    <div className="contact-status-indicator contact-status-red">
                                        <div className="contact-status-dot"></div>
                                    </div>
                                </div>
                                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">Email Support</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center flex-1 px-6">
                                <CardDescription className="text-gray-600 leading-relaxed text-base mb-8">
                                    Send us detailed information about your requirements and we'll respond promptly with comprehensive solutions.
                                </CardDescription>
                            </CardContent>
                            <CardFooter className="flex flex-col space-y-3 pt-0 px-6 pb-6">

                                <ContactFormSheet
                                    icon={<Mail className="w-5 h-5 mr-2" />}
                                    buttonText="Send Email"
                                    buttonClassName="btn w-full md:w-full bg-harvey-red hover:bg-harvey-red/90 text-white font-semibold py-3 px-6 rounded-xl text-lg h-12" />
                                <p className="text-xs text-gray-500 break-all">
                                    Mon-Fri 8:30AM - 5:30PM NZST
                                </p>
                            </CardFooter>
                        </Card>
                    </div>

                    {/* Location */}
                    <div className="group relative h-full">
                        <Card className="h-full min-h-[450px] bg-gray-100 hover:shadow-xl transition-all duration-300 border-gray-100 hover:border-harvey-red/20 transform hover:-translate-y-2 flex flex-col">
                            <CardHeader className="text-center pb-4">
                                <div className="relative flex justify-center mb-4">
                                    <div className="contact-icon-location">
                                        <Building2 className="w-10 h-10 text-white" />
                                    </div>
                                    <div className="contact-status-indicator contact-status-red">
                                        <div className="contact-status-dot"></div>
                                    </div>
                                </div>
                                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">Visit Our Showroom</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center flex-1 px-6">
                                <CardDescription className="text-gray-600 leading-relaxed text-base mb-8">
                                    Experience our technology solutions firsthand in our state-of-the-art showrooms across New Zealand.
                                </CardDescription>
                            </CardContent>
                            <CardFooter className="flex flex-col space-y-3 pt-0 px-6 pb-6">
                                <Button
                                    asChild
                                    className="w-full bg-harvey-red hover:bg-harvey-red/90 text-white font-semibold py-3 px-6 rounded-xl text-lg h-12 btn"
                                >
                                    <SafeLink to="/showroom">
                                        <MapPin className="w-5 h-5 mr-2" />
                                        Find Locations
                                    </SafeLink>
                                </Button>
                                <p className="text-xs text-gray-500">
                                    Multiple locations across NZ
                                </p>
                            </CardFooter>
                        </Card>
                    </div>

                    {/* LinkedIn */}
                    <div className="group relative h-full">
                        <Card className="h-full min-h-[450px] bg-gray-100 hover:shadow-xl transition-all duration-300 border-gray-100 hover:border-harvey-red/20 transform hover:-translate-y-2 flex flex-col">
                            <CardHeader className="text-center pb-4">
                                <div className="relative flex justify-center mb-4">
                                    <div className="contact-icon-linkedin">
                                        <Linkedin className="w-10 h-10 text-white" />
                                    </div>
                                    <div className="contact-status-indicator contact-status-red">
                                        <div className="contact-status-dot"></div>
                                    </div>
                                </div>
                                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">Connect on LinkedIn</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center flex-1 px-6">
                                <CardDescription className="text-gray-600 leading-relaxed text-base mb-8">
                                    Follow us for industry insights, technology trends, and business solutions. Connect with our professional network.
                                </CardDescription>
                            </CardContent>
                            <CardFooter className="flex flex-col space-y-3 pt-0 px-6 pb-6">
                                <Button
                                    asChild
                                    className="w-full bg-harvey-red hover:bg-harvey-red/90 text-white font-semibold py-3 px-6 rounded-xl text-lg h-12 btn"
                                >
                                    <a href="https://www.linkedin.com/showcase/harvey-norman-commercial-nz/" target="_blank" rel="noopener noreferrer">
                                        <Linkedin className="w-5 h-5 mr-2" />
                                        Follow Us
                                    </a>
                                </Button>
                                <p className="text-xs text-gray-500">
                                    Stay updated with latest insights
                                </p>
                            </CardFooter>
                        </Card>
                    </div>
                </div>
            </Section>
           
            {/* For Testing Products */}
            {/* <Section>
                <Button asChild>
                    <SafeLink to="/products" className="uppercase text-2xl font-bold tracking-wide">
                        Products
                    </SafeLink>
                </Button>                
            </Section> */}

            {/* testing appliance2 */}
             {/* <Section>
                <Button asChild>
                    <SafeLink to="/appliances-2" className="uppercase text-2xl font-bold tracking-wide">
                        Appliances 2
                    </SafeLink>
                </Button> 
                <Button asChild>
                    <SafeLink to="/appliances-3" className="uppercase text-2xl font-bold tracking-wide">
                        Appliances 3
                    </SafeLink>
                </Button>   
                <Button asChild>
                    <SafeLink to="/appliances-4" className="uppercase text-2xl font-bold tracking-wide">
                        Appliances 4
                    </SafeLink>
                </Button> 
                <Button asChild>
                    <SafeLink to="/appliances-5-webp-darker" className="uppercase text-2xl font-bold tracking-wide">
                        Appliances 5
                    </SafeLink>
                </Button>                    
            </Section> */}
            
             

                {/* Product Dropdown Menu */}
            {/* <Section>                
                <ShowcaseSubmenuDropdown />
            </Section> */}
            
            {/* for testing Bathroom and Plumbing page */}
            {/* <Section>                
                <Button asChild>
                    <SafeLink to="/bathrooms" className="uppercase text-2xl font-bold tracking-wide">
                        Bathrooms
                    </SafeLink>
                </Button>   
            </Section> */}
            
            {/* For testing Draft404 */}
            {/* <Section>
                <Button asChild>
                    <SafeLink to="/draft-404" className="uppercase text-2xl font-bold tracking-wide">
                        Draft 404
                    </SafeLink>
                </Button>
            </Section> */}
            {/* test Email Preferences */}
            {/* <Section scrollSnap={true} variant="default" padding="none">
                <EmailPreferenceForm />
            </Section> */}

            {/*  test Kitchen Page */}
            {/* <Section>
                <Button asChild>
                    <SafeLink to="/kitchens/" className="uppercase text-2xl font-bold tracking-wide">
                        Kitchen
                    </SafeLink>
                </Button>                
            </Section> */}

            

            {/* CTA Section */}
            <Section scrollSnap={true} variant="default" padding="none">

            </Section>
            <CTAGeneric
                description="Join thousands of satisfied commercial clients who trust Harvey Norman Commercial with their business needs." />
        </ScrollContainer>

    );
}
